import { FilterListRounded } from "@mui/icons-material";
import { Badge, Button, Menu, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import _ from "lodash";
import { useState } from "react";
import { ToggleButtonSubmenu } from "src/components";
import { UserFeedbackFilterByOption, UserFeedbackGroupByOption } from "src/constants";
import { type FormSubmissionDto } from "src/http";

interface Props {
  items: FormSubmissionDto[];
  groupBy: UserFeedbackGroupByOption;
  filters: Record<UserFeedbackFilterByOption, string[]>;
  disabled?: boolean;
  enableFilterByReceiver?: boolean;
  enableFilterBySender?: boolean;
  onGroupByChanged: (value: UserFeedbackGroupByOption) => void;
  onFilterByChanged: (filters: Record<UserFeedbackFilterByOption, string[]>) => void;
}

const UserFeedbackFilterButton = (props: Props) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  function handleFilterByOptionChanged(options: string[]) {
    // Ignore other options that require a value selection from a submenu
    if (options.includes("All")) {
      props.onFilterByChanged({
        [UserFeedbackFilterByOption.FormName]: [],
        [UserFeedbackFilterByOption.Sender]: [],
        [UserFeedbackFilterByOption.Receiver]: [],
        [UserFeedbackFilterByOption.RequiresReviewInOneOnOne]: [],
        [UserFeedbackFilterByOption.ReviewedInOneOnOne]: [],
        [UserFeedbackFilterByOption.ExcludeDrafts]: [],
      });
    }
  }

  function handleFilterByValueChanged(option: UserFeedbackFilterByOption, values: string[]) {
    if (values != null) {
      props.onFilterByChanged({
        ...props.filters,
        [option]: values,
      });
    }
  }

  const formTypeOptions = _.uniq(props.items.map((x) => x.formName)).sort((a, b) => a.localeCompare(b));

  const senderOptions = _.uniqBy(
    props.items.map((x) => x.createdByUser),
    (x) => x.userId,
  ).sort((a, b) => a.displayName.localeCompare(b.displayName));

  const receiverOptions = _.uniqBy(
    props.items.map((x) => x.recipientUser ?? x.createdByUser),
    (x) => x.userId,
  ).sort((a, b) => {
    return a.displayName.localeCompare(b.displayName);
  });

  const appliedFilters = [
    UserFeedbackFilterByOption.FormName,
    UserFeedbackFilterByOption.Sender,
    UserFeedbackFilterByOption.Receiver,
  ].filter((x) => {
    return props.filters[x]?.length > 0;
  });

  return (
    <>
      <Badge color="primary" variant="dot" invisible={appliedFilters.length === 0}>
        <Button
          variant="tertiary"
          disabled={props.disabled}
          sx={{ minWidth: 0, p: 0.25 }}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        >
          <FilterListRounded />
        </Button>
      </Badge>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        slotProps={{
          paper: { sx: { minWidth: 200 } },
        }}
        anchorOrigin={{
          horizontal: "right",
          vertical: "bottom",
        }}
        transformOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
      >
        <Typography variant="overline" sx={{ mx: 0.5 }}>
          Group By
        </Typography>
        <ToggleButtonGroup
          value={props.groupBy}
          exclusive
          orientation="vertical"
          variant="menu"
          onChange={(_, value) => {
            if (value != null) {
              props.onGroupByChanged(value);
            }
          }}
          sx={{ minWidth: "100%" }}
        >
          <ToggleButton value={UserFeedbackGroupByOption.None}>
            <Typography>None</Typography>
          </ToggleButton>
          <ToggleButton value={UserFeedbackGroupByOption.Type}>
            <Typography>Type</Typography>
          </ToggleButton>
          <ToggleButton value={UserFeedbackGroupByOption.Sender}>
            <Typography>Sender</Typography>
          </ToggleButton>
          <ToggleButton value={UserFeedbackGroupByOption.State}>
            <Typography>State</Typography>
          </ToggleButton>
        </ToggleButtonGroup>

        <Typography variant="overline" sx={{ mx: 0.5, mt: 1 }}>
          Filter By
        </Typography>
        <ToggleButtonGroup
          value={appliedFilters.length > 0 ? appliedFilters : ["All"]}
          orientation="vertical"
          variant="menu"
          onChange={(_, values) => handleFilterByOptionChanged(values)}
          sx={{ minWidth: "100%" }}
        >
          <ToggleButton value={"All"}>
            <Typography>All</Typography>
          </ToggleButton>
          <ToggleButtonSubmenu
            disabled={formTypeOptions.length <= 1}
            label="Type"
            value={UserFeedbackFilterByOption.FormName}
            suboptions={formTypeOptions}
            selectedSuboptionValues={props.filters[UserFeedbackFilterByOption.FormName] ?? []}
            onSelected={(selectedSuboptionValues) =>
              handleFilterByValueChanged(UserFeedbackFilterByOption.FormName, selectedSuboptionValues)
            }
          />

          {props.enableFilterBySender && (
            <ToggleButtonSubmenu
              disabled={senderOptions.length <= 1}
              label="Sender"
              value={UserFeedbackFilterByOption.Sender}
              suboptions={senderOptions}
              selectedSuboptionValues={props.filters[UserFeedbackFilterByOption.Sender] ?? []}
              onSelected={(suboptions) => handleFilterByValueChanged(UserFeedbackFilterByOption.Sender, suboptions)}
              suboptionLabelSelector={(x) => x.displayName}
              suboptionValueSelector={(x) => x.userId}
            />
          )}

          {props.enableFilterByReceiver && (
            <ToggleButtonSubmenu
              disabled={receiverOptions.length <= 1}
              label="Receiver"
              value={UserFeedbackFilterByOption.Receiver}
              suboptions={receiverOptions}
              selectedSuboptionValues={props.filters[UserFeedbackFilterByOption.Receiver] ?? []}
              onSelected={(suboptions) => handleFilterByValueChanged(UserFeedbackFilterByOption.Receiver, suboptions)}
              suboptionLabelSelector={(x) => x?.displayName ?? "None"}
              suboptionValueSelector={(x) => x?.userId ?? ""}
            />
          )}
        </ToggleButtonGroup>
      </Menu>
    </>
  );
};

export { UserFeedbackFilterButton };
